import { _IdGenerator, FocusTrap, FocusTrapFactory } from '@angular/cdk/a11y';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import {
  booleanAttribute,
  ComponentRef,
  DestroyRef,
  Directive,
  effect,
  ElementRef,
  inject,
  input,
  model,
  NgZone,
  TemplateRef,
  untracked,
} from '@angular/core';
import { fromEvent, Subscription, takeUntil } from 'rxjs';
import { connectedPositions } from '../utils/connected-positions';
import { PopoverPanelComponent } from './popover-panel.component';

export type PopoverPosition = 'above' | 'below';

const OFFSET = 4;

const SIDE: Record<PopoverPosition, 'top' | 'bottom'> = { above: 'top', below: 'bottom' };

function isScrolledOutOfView(trigger: Element, scroller: Element): boolean {
  const triggerRect = trigger.getBoundingClientRect();
  const scrollerRect = scroller.getBoundingClientRect();
  return (
    triggerRect.bottom < scrollerRect.top ||
    triggerRect.top > scrollerRect.bottom ||
    triggerRect.right < scrollerRect.left ||
    triggerRect.left > scrollerRect.right
  );
}

@Directive({
  selector: '[zagPopover]',
  exportAs: 'zagPopover',
  host: {
    'aria-haspopup': 'dialog',
    '[attr.aria-expanded]': 'popoverOpen()',
    '[attr.aria-controls]': 'panelId',
    '(click)': 'toggle()',
  },
})
export class PopoverDirective {
  private readonly overlay = inject(Overlay);
  private readonly focusTrapFactory = inject(FocusTrapFactory);
  private readonly ngZone = inject(NgZone);
  private readonly trigger = inject<ElementRef<HTMLElement>>(ElementRef).nativeElement;

  protected readonly panelId = inject(_IdGenerator).getId('zag-popover-');

  readonly zagPopover = input.required<TemplateRef<unknown>>();

  readonly popoverLabel = input.required<string>();

  readonly popoverPosition = input<PopoverPosition>('below');

  readonly popoverFocusContent = input(true, { transform: booleanAttribute });

  readonly popoverCloseOnOutsideClick = input(true, { transform: booleanAttribute });

  readonly popoverOpen = model(false);

  private overlayRef: OverlayRef | null = null;
  private panel: ComponentRef<PopoverPanelComponent> | null = null;
  private overlaySubscriptions: Subscription | null = null;
  private focusTrap: FocusTrap | null = null;

  constructor() {
    effect(() => {
      const open = this.popoverOpen();
      const content = this.zagPopover();
      const label = this.popoverLabel();
      untracked(() => {
        if (open) this.attach();
        else this.detach();
        this.panel?.setInput('content', content);
        this.panel?.setInput('label', label);
      });
    });
    inject(DestroyRef).onDestroy(() => this.detach());
  }

  protected toggle(): void {
    this.popoverOpen.update(open => !open);
  }

  private attach(): void {
    if (this.overlayRef) return;

    // Preferred side first; the end-aligned and opposite-side fallbacks keep the panel inside the viewport.
    const overlayRef = this.overlay.create({
      positionStrategy: this.overlay
        .position()
        .flexibleConnectedTo(this.trigger)
        .withPositions(connectedPositions(SIDE[this.popoverPosition()], 'start', OFFSET))
        .withFlexibleDimensions(false)
        .withPush(true),
    });
    this.overlayRef = overlayRef;

    const panel = overlayRef.attach(new ComponentPortal(PopoverPanelComponent));
    this.panel = panel;
    panel.setInput('panelId', this.panelId);

    const subscriptions = new Subscription();
    subscriptions.add(overlayRef.detachments().subscribe(() => this.popoverOpen.set(false)));
    subscriptions.add(
      overlayRef.keydownEvents().subscribe(event => {
        if (event.key !== 'Escape') return;
        event.preventDefault();
        this.popoverOpen.set(false);
        this.trigger.focus();
      })
    );
    subscriptions.add(
      overlayRef.outsidePointerEvents().subscribe(event => {
        if (this.popoverCloseOnOutsideClick() && !this.trigger.contains(event.target as Node)) {
          this.popoverOpen.set(false);
        }
      })
    );
    this.overlaySubscriptions = subscriptions;

    this.followTrigger(overlayRef);
    if (this.popoverFocusContent()) this.focusContent(panel.location.nativeElement);
  }

  // CDK scroll strategies only see registered cdkScrollable containers, so listen for any ancestor scroll in the capture phase.
  private followTrigger(overlayRef: OverlayRef): void {
    this.ngZone.runOutsideAngular(() => {
      fromEvent(this.trigger.ownerDocument, 'scroll', { capture: true, passive: true })
        .pipe(takeUntil(overlayRef.detachments()))
        .subscribe(event => {
          const scroller = event.target as Node;
          if (!scroller.contains(this.trigger)) return;
          if (scroller instanceof Element && isScrolledOutOfView(this.trigger, scroller)) {
            this.ngZone.run(() => this.popoverOpen.set(false));
          } else {
            overlayRef.updatePosition();
          }
        });
    });
  }

  private detach(): void {
    const overlayRef = this.overlayRef;
    if (!overlayRef) return;
    this.overlayRef = null;
    this.panel = null;

    const focusWasInside = overlayRef.overlayElement.contains(this.trigger.ownerDocument.activeElement);
    this.overlaySubscriptions?.unsubscribe();
    this.overlaySubscriptions = null;
    this.focusTrap?.destroy();
    this.focusTrap = null;
    overlayRef.dispose();
    if (focusWasInside) this.trigger.focus({ preventScroll: true });
  }

  private focusContent(panel: HTMLElement): void {
    const focusTrap = this.focusTrapFactory.create(panel, true);
    focusTrap.attachAnchors();
    this.focusTrap = focusTrap;
    void focusTrap.focusInitialElementWhenReady().then(focused => {
      if (!focused) panel.focus();
    });
  }
}
